"use client";

import React, { useEffect, useRef, useState } from "react";
import {
  CheckCircle,
  SkipForward,
  X,
  Ban,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import axios from "axios";
import { base_url } from "../../config";
import BadgeButton from "../../components/BadgeButton/BadgeButton";

interface CancellationRow {
  code: string;
  name: string;
  email: string;
  type: string;
  reason: string | null;
  comments: string | null;
  paid: number;
  fee: number;
  refund: number;
  stripe_intent_count: number;
}

interface CancellationRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  row: CancellationRow | null;
  onApprove: () => void | Promise<void>;
  onSkip?: () => void | Promise<void>;
}

const typeChip = (value: string | undefined) => {
  switch (value) {
    case "industry":
      return { label: "Industry day", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" };
    case "regular":
      return { label: "Regular delegate", cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" };
    case "student":
      return { label: "Student", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" };
    case "student-paper":
      return { label: "Student with paper", cls: "bg-violet-500/15 text-violet-300 border-violet-500/30" };
    case "exhibitor":
      return { label: "Exhibitor", cls: "bg-orange-500/15 text-orange-300 border-orange-500/30" };
    default:
      return { label: value || "—", cls: "bg-slate-500/15 text-slate-300 border-slate-500/30" };
  }
};

const money = (n: number | null | undefined) =>
  typeof n === "number" && !Number.isNaN(n)
    ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n)
    : "—";

export default function CancellationRequestModal({
  isOpen,
  row,
  onClose,
  onApprove,
  onSkip,
}: CancellationRequestModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        handleClose();
      }
    }
    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        handleClose();
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "auto";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) setError(null);
  }, [isOpen, row?.code]);

  const handleApprove = async () => {
    if (!row) return;
    try {
      setIsLoading(true);
      setError(null);
      await axios.post(`${base_url}/api/admin/approve_cancellation`, {
        code: row.code,
      });
      await Promise.resolve(onApprove());
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to approve the cancellation request.";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  if (!isOpen || !row) return null;

  const tchip = typeChip(row.type);
  const blocked = row.stripe_intent_count !== 1;

  return (
    <div className="dark fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div
        ref={modalRef}
        className="relative flex flex-col bg-slate-900 text-slate-100 border border-slate-700/50 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cancellation-modal-title"
      >
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 inline-flex items-center justify-center h-8 w-8 rounded-md text-slate-400 hover:text-slate-100 hover:bg-slate-700/50 transition-colors z-10"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 text-blue-400 animate-spin" />
          </div>
        ) : (
          <>
            <header className="px-5 py-3 border-b border-slate-700/40 bg-slate-900/40 flex items-center gap-3 pr-12">
              <Ban className="h-5 w-5 text-rose-400 shrink-0" />
              <div className="flex flex-col min-w-0">
                <h2
                  id="cancellation-modal-title"
                  className="text-base font-semibold text-slate-100 leading-tight truncate"
                >
                  {row.name || "Cancellation request"}
                </h2>
                <span className="text-xs text-slate-400 truncate">{row.email}</span>
              </div>
              <div className="ml-auto flex items-center gap-2 shrink-0">
                <BadgeButton code={row?.code} />
                <span className="font-mono text-[11px] px-2 py-0.5 rounded-md bg-slate-700/40 text-slate-200 border border-slate-600/40">
                  {row.code}
                </span>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium ${tchip.cls}`}
                >
                  {tchip.label}
                </span>
              </div>
            </header>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              <section className="rounded-xl border border-slate-700/40 bg-slate-800/40 overflow-hidden">
                <header className="px-4 py-2 border-b border-slate-700/40 bg-slate-900/40">
                  <h3 className="text-[11px] uppercase tracking-wider font-semibold text-slate-300">
                    Delegate's request
                  </h3>
                </header>
                <div className="p-4 space-y-3 text-sm">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-[11px] uppercase tracking-wider text-slate-400 shrink-0">
                      Reason
                    </span>
                    <span className="text-slate-100 text-right">
                      {row.reason || <span className="text-slate-500">—</span>}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-[11px] uppercase tracking-wider text-slate-400">
                      Comments
                    </span>
                    <p className="text-slate-200 text-sm whitespace-pre-wrap rounded-md bg-slate-900/60 border border-slate-700/40 px-3 py-2 min-h-[3rem]">
                      {row.comments || <span className="text-slate-500">No additional comments.</span>}
                    </p>
                  </div>
                </div>
              </section>

              <section className="rounded-xl border border-slate-700/40 bg-slate-800/40 overflow-hidden">
                <header className="px-4 py-2 border-b border-slate-700/40 bg-slate-900/40">
                  <h3 className="text-[11px] uppercase tracking-wider font-semibold text-slate-300">
                    Refund preview
                  </h3>
                </header>
                <div className="p-4 text-sm">
                  <ul className="divide-y divide-slate-700/40">
                    <li className="flex justify-between py-1.5">
                      <span className="text-slate-300">Paid</span>
                      <span className="font-mono text-slate-100">{money(row.paid)}</span>
                    </li>
                    <li className="flex justify-between py-1.5">
                      <span className="text-slate-300">Cancellation fee</span>
                      <span className="font-mono text-rose-300">- {money(row.fee)}</span>
                    </li>
                    <li className="flex justify-between py-2 border-t border-slate-700/40 mt-1 pt-3">
                      <span className="text-slate-200 font-medium">Refund to delegate</span>
                      <span className="font-mono text-lg font-bold text-emerald-300">
                        {money(row.refund)}
                      </span>
                    </li>
                  </ul>
                </div>
              </section>

              {blocked && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-200 text-xs">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <p>
                    This registration has {row.stripe_intent_count} Stripe payment intents.
                    Only single-intent refunds can be processed here — use the artisan
                    command for manual handling.
                  </p>
                </div>
              )}

              {error && (
                <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-rose-200 text-xs">
                  {error}
                </div>
              )}
            </div>

            <div className="px-5 py-3 border-t border-slate-700/40 bg-slate-900/40 flex items-center justify-end gap-2">
              {onSkip && (
                <button
                  onClick={async () => {
                    await Promise.resolve(onSkip());
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-slate-600/30 text-slate-200 border border-slate-600/40 hover:bg-slate-600/50 transition-colors"
                >
                  <SkipForward className="h-3.5 w-3.5" />
                  Skip
                </button>
              )}
              <button
                onClick={handleApprove}
                disabled={blocked}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-emerald-500/20 text-emerald-200 border border-emerald-500/30 hover:bg-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <CheckCircle className="h-3.5 w-3.5" />
                Approve & refund
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
